// Relative path: src/components/Recommendations/ReviewGrid.js

import React, { useState, useEffect } from "react";
import recUtils from "../../utils/RecUtils";
import ReviewCard from "../ReviewCard";
import ReviewMetaLine from "../ReviewMetaLine";
import websiteUtils from "../../utils/WebsiteUtils";
import unknownUser from "../../images/unknown_user.svg";

/** Pull satisfaction & similarity from our justification tables */
function findReviewMetaData(reviewId, tables) {
  for (const table of tables) {
    if (!Array.isArray(table.data)) continue;
    for (const row of table.data) {
      if (String(row.review_id).trim() === String(reviewId).trim()) {
        return {
          satisfaction: row.user_satisfaction ?? -1,
          similarity: row.overall_similarity ?? -1
        };
      }
    }
  }
  return { satisfaction: -1, similarity: -1 };
}

export default function ReviewGrid({ clinicOnlyName, isFallbackMode = false }) {
  const [reviewItems, setReviewItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (isFallbackMode) {
        // Load reviews directly from reviews collection in fallback mode
        await loadFallbackReviews();
      } else {
        // Normal mode - load from recommendation justification tables
        const recDoc = recUtils.recommendationDoc;
        if (!recDoc) return;

        const tables = recDoc.clinic_justification_reviews_tables || [];
        const idSet = new Set();
        tables.forEach((table) =>
          table.data.forEach((row) => {
            if (
              row.clinic_only_name?.trim() === clinicOnlyName.trim() &&
              row.review_id
            ) {
              idSet.add(row.review_id);
            }
          })
        );
        const ids = Array.from(idSet);
        if (ids.length === 0) {
          setReviewItems([]);
          return;
        }

        setLoading(true);
        try {
          const docs = await Promise.all(
            ids.map((reviewId) => recUtils.fetchReviewFromId(reviewId))
          );

          const rawItems = docs.map((doc, idx) => {
            if (!doc) return null;
            const reviewId = ids[idx];
            const { satisfaction, similarity } = findReviewMetaData(
              reviewId,
              tables
            );

            if (doc.redditEmbedLink) {
              const div = document.createElement("div");
              div.innerHTML = doc.redditEmbedLink;
              const bq = div.querySelector("blockquote.reddit-embed-bq");
              const anchor = bq?.querySelector("a[href]");
              const path = anchor ? new URL(anchor.href).pathname : "";
              const embedHeight =
                parseInt(bq?.dataset.embedHeight || "500", 10) || 500;

              return {
                type: "reddit",
                id: reviewId,
                redditPath: path,
                embedHeight,
                satisfaction,
                similarity,
                siteForPriority: "reddit"
              };
            }

            // normal card - use centralized website detection
            const websiteInfo = websiteUtils.getWebsiteSourceInfo(doc);
            const websiteKey = websiteInfo.websiteKey;
            
            let logoSrc = websiteUtils.getLogo(websiteKey);

            let imageSrc = doc.thumbnailImage?.[0]?.downloadURL || null;
            let imageClassName = "";
            if (websiteKey !== "youtube" && websiteKey !== "tiktok") {
              imageSrc = unknownUser;
              imageClassName = "opacity-40 mt-8";
            }

            let authorImg = doc.logoImage?.[0]?.downloadURL || null;
            if (websiteKey !== "reddit" && websiteKey !== "youtube" && websiteKey !== "tiktok") {
              authorImg = null;
            }

            const link = doc.is_external
              ? doc.external_url || "#"
              : `/review/${reviewId}`;

            return {
              type: "normal",
              id: reviewId,
              title: (
                <span>
                  Website: <strong>{websiteInfo.displayName}</strong>
                </span>
              ),
              author: doc.reviewer_name || "Unknown",
              link,
              sourceUrl: link,
              imageSrc,
              logoSrc,
              authorImg,
              imageClassName,
              satisfaction,
              similarity,
              siteForPriority: websiteInfo.displayName
            };
          });

          const items = rawItems
            .filter((it) => it !== null)
            .sort((a, b) => websiteUtils.getWebsitePriority(a) - websiteUtils.getWebsitePriority(b));

          setReviewItems(items);
        } catch (err) {
          console.error("Error loading review items:", err);
          setReviewItems([]);
        } finally {
          setLoading(false);
        }
      }
    };

    load();
  }, [clinicOnlyName, isFallbackMode]);

  // Helper function to load reviews in fallback mode
  const loadFallbackReviews = async () => {
    setLoading(true);
    try {
      // Fetch reviews directly from reviews collection
      const reviews = await recUtils.fetchFallbackReviews(clinicOnlyName, 20);
      
      if (!reviews || reviews.length === 0) {
        setReviewItems([]);
        setLoading(false);
        return;
      }
      
      // Process each review into the format expected by the component
      const rawItems = reviews.map(doc => {
        if (!doc) return null;
        const reviewId = doc.id;
        
        // For reddit reviews with embed link
        if (doc.redditEmbedLink) {
          const div = document.createElement("div");
          div.innerHTML = doc.redditEmbedLink;
          const bq = div.querySelector("blockquote.reddit-embed-bq");
          const anchor = bq?.querySelector("a[href]");
          const path = anchor ? new URL(anchor.href).pathname : "";
          const embedHeight =
            parseInt(bq?.dataset.embedHeight || "500", 10) || 500;

          return {
            type: "reddit",
            id: reviewId,
            redditPath: path,
            embedHeight,
            satisfaction: doc.user_satisfaction || 4, // Default to 4 for fallback mode
            similarity: -1, // No similarity in fallback mode
            siteForPriority: "reddit"
          };
        }

        // For normal reviews - use centralized website detection
        const websiteInfo = websiteUtils.getWebsiteSourceInfo(doc);
        const websiteKey = websiteInfo.websiteKey;
        
        let logoSrc = websiteUtils.getLogo(websiteKey);

        let imageSrc = doc.thumbnailImage?.[0]?.downloadURL || null;
        let imageClassName = "";
        if (websiteKey !== "youtube" && websiteKey !== "tiktok") {
          imageSrc = unknownUser;
          imageClassName = "opacity-40 mt-8";
        }

        let authorImg = doc.logoImage?.[0]?.downloadURL || null;
        if (websiteKey !== "reddit" && websiteKey !== "youtube" && websiteKey !== "tiktok") {
          authorImg = null;
        }

        const link = doc.is_external
          ? doc.external_url || "#"
          : `/review/${reviewId}`;

        return {
          type: "normal",
          id: reviewId,
          title: (
            <span>
              Website: <strong>{websiteInfo.displayName}</strong>
            </span>
          ),
          author: doc.reviewer_name || "Unknown",
          link,
          sourceUrl: link,
          imageSrc,
          logoSrc,
          authorImg,
          imageClassName,
          satisfaction: doc.user_satisfaction || 4, // Default to 4 for fallback mode
          similarity: -1, // No similarity in fallback mode
          siteForPriority: websiteInfo.displayName
        };
      });

      const items = rawItems
        .filter((it) => it !== null)
        .sort((a, b) => websiteUtils.getWebsitePriority(a) - websiteUtils.getWebsitePriority(b));

      setReviewItems(items);
    } catch (err) {
      console.error("Error loading fallback review items:", err);
      setReviewItems([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <p className="text-center text-gray-600 py-4">Loading reviews...</p>;
  }
  
  // In fallback mode, display special message if no reviews
  if (!loading && reviewItems.length === 0) {
    if (isFallbackMode) {
      return (
        <div className="mt-10 mb-10">
          <p className="text-center text-gray-600 py-4">
            No reviews available for this clinic.
          </p>
        </div>
      );
    } else {
      return (
        <p className="text-center text-red-500 py-4">
          No matching reviews found for {clinicOnlyName}.
        </p>
      );
    }
  }

  return (
    <div>
      <h2 className="text-4xl font-extrabold text-mm_blue text-center mt-2 mb-3">
        {isFallbackMode ? "Clinic Reviews" : "Corresponding Patient Reviews"}
      </h2>
      <h3 className="text-l text-center mb-6">
        {isFallbackMode
          ? `${clinicOnlyName} patient reviews from across the web.`
          : clinicOnlyName
            ? "Our recommendations are based on the independent patient reviews below."
            : "Please select a clinic to see reviews."}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-min mb-36 md:mb-0">
        {reviewItems.map((item) => (
          <div
            key={item.id}
            className="relative bg-transparent rounded-lg w-full flex flex-col gap-2 pt-6 overflow-visible"
          >
            {/* 1) Bubble: z-0 */}
            <div
              aria-hidden="true"
              className="absolute mr-16 inset-x-2 -top-0 h-48 bg-transparent border-t-2 border-x-2 border-gray-200 rounded-3xl z-0"
            />

            {/* 2) Meta-line: z-10 */}
            <div className="relative inline-block mr-16 px-3 z-10">
              <ReviewMetaLine
                similarity={isFallbackMode ? -1 : item.similarity}
                satisfaction={item.satisfaction}
                isFallbackMode={isFallbackMode}
              />
            </div>

            {/* 3) Review content: z-20 */}
            <div className="relative z-20">
              {item.type === "reddit" ? (
                <a
                  href={`https://www.reddit.com${item.redditPath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <iframe
                    src={`https://www.redditmedia.com${item.redditPath}?embed=true`}
                    style={{
                      width: "100%",
                      height: `${item.embedHeight}px`,
                      border: 0,
                      pointerEvents: "none"
                    }}
                    scrolling="no"
                    frameBorder="0"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                </a>
              ) : (
                <ReviewCard
                  title={item.title}
                  author={item.author}
                  link={item.link}
                  sourceUrl={item.sourceUrl}
                  imageSrc={item.imageSrc}
                  logoSrc={item.logoSrc}
                  authorImg={item.authorImg}
                  imageClassName={item.imageClassName}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}